import { useEffect, useMemo, useState } from "react";

import PageHeader from "../components/PageHeader.jsx";
import StatCard from "../components/StatCard.jsx";
import apiClient from "../services/api.js";

const moneyFormatter = new Intl.NumberFormat("en-NG", {
  style: "currency",
  currency: "NGN",
  maximumFractionDigits: 2
});

const sections = [
  { id: "overview", label: "Overview" },
  { id: "members", label: "Members & KYC" },
  { id: "products", label: "Products" },
  { id: "savings", label: "Savings & Wallets" },
  { id: "loans", label: "Loans" },
  { id: "accounting", label: "Accounting" },
  { id: "communications", label: "Comms" }
];

const memberStatuses = ["active", "suspended", "inactive"];
const loanStatuses = ["pending", "approved", "rejected", "disbursed", "active", "closed"];

const emptyWalletForm = {
  member_id: "",
  type: "top-up",
  amount: "",
  note: ""
};

function formatMoney(value) {
  return moneyFormatter.format(Number.parseFloat(value || "0"));
}

function formatDate(value) {
  if (!value) return "N/A";
  return new Date(value).toLocaleDateString("en-NG", {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

function labelize(value) {
  return String(value || "none").replace(/_/g, " ").toUpperCase();
}

function memberName(member) {
  if (!member) return "Unknown member";
  return `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email;
}

function sumAmount(rows, field) {
  return rows.reduce((sum, row) => sum + Number.parseFloat(row?.[field] || "0"), 0);
}

function latestKycForMember(memberId, kyc) {
  return kyc.find((entry) => Number(entry.member_id) === Number(memberId));
}

function StatusBadge({ value }) {
  const status = value || "unknown";
  const tone =
    status === "active" || status === "approved" || status === "disbursed"
      ? "bg-emerald-50 text-emerald-700"
      : status === "pending" || status === "submitted" || status === "needs_more_info"
        ? "bg-amber-50 text-amber-700"
        : "bg-rose-50 text-rose-700";
  return (
    <span className={`rounded-xl px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] ${tone}`}>
      {labelize(status)}
    </span>
  );
}

export default function AdminPage() {
  const [activeSection, setActiveSection] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState([]);
  const [kyc, setKyc] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [savings, setSavings] = useState([]);
  const [loans, setLoans] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [thriftPlans, setThriftPlans] = useState([]);
  const [loanProducts, setLoanProducts] = useState([]);
  const [agentCollections, setAgentCollections] = useState([]);
  const [investmentPortfolios, setInvestmentPortfolios] = useState([]);
  const [dividendRuns, setDividendRuns] = useState([]);
  const [ajoCircles, setAjoCircles] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberModalOpen, setMemberModalOpen] = useState(false);
  const [memberDetailLoading, setMemberDetailLoading] = useState(false);
  const [walletForm, setWalletForm] = useState(emptyWalletForm);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [savingAction, setSavingAction] = useState(false);

  const loadData = async () => {
    setLoading(true);
    setError("");
    const endpoints = {
      overview: apiClient.get("/admin/overview"),
      members: apiClient.get("/admin/members"),
      kyc: apiClient.get("/admin/kyc"),
      contributions: apiClient.get("/admin/contributions"),
      savings: apiClient.get("/admin/savings"),
      loans: apiClient.get("/admin/loans"),
      transactions: apiClient.get("/admin/wallet-transactions"),
      auditLogs: apiClient.get("/admin/audit-logs"),
      thriftPlans: apiClient.get("/admin/thrift-plans"),
      loanProducts: apiClient.get("/admin/loan-products"),
      agentCollections: apiClient.get("/admin/agent-collections"),
      investmentPortfolios: apiClient.get("/admin/investment-portfolios"),
      dividendRuns: apiClient.get("/admin/dividend-runs"),
      ajoCircles: apiClient.get("/admin/ajo-circles")
    };
    const results = await Promise.allSettled(Object.values(endpoints));
    const keys = Object.keys(endpoints);
    const next = keys.reduce((acc, key, index) => {
      acc[key] = results[index];
      return acc;
    }, {});

    if (next.overview.status === "fulfilled") setOverview(next.overview.value.data);
    if (next.members.status === "fulfilled") setMembers(next.members.value.data || []);
    if (next.kyc.status === "fulfilled") setKyc(next.kyc.value.data || []);
    if (next.contributions.status === "fulfilled") setContributions(next.contributions.value.data || []);
    if (next.savings.status === "fulfilled") setSavings(next.savings.value.data || []);
    if (next.loans.status === "fulfilled") setLoans(next.loans.value.data || []);
    if (next.transactions.status === "fulfilled") setTransactions(next.transactions.value.data || []);
    if (next.auditLogs.status === "fulfilled") setAuditLogs(next.auditLogs.value.data || []);
    if (next.thriftPlans.status === "fulfilled") setThriftPlans(next.thriftPlans.value.data || []);
    if (next.loanProducts.status === "fulfilled") setLoanProducts(next.loanProducts.value.data || []);
    if (next.agentCollections.status === "fulfilled") setAgentCollections(next.agentCollections.value.data || []);
    if (next.investmentPortfolios.status === "fulfilled") setInvestmentPortfolios(next.investmentPortfolios.value.data || []);
    if (next.dividendRuns.status === "fulfilled") setDividendRuns(next.dividendRuns.value.data || []);
    if (next.ajoCircles.status === "fulfilled") setAjoCircles(next.ajoCircles.value.data || []);

    if (results.every((result) => result.status === "rejected")) {
      setError("Unable to load operations data.");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadData();
  }, []);

  const memberOptions = useMemo(() => {
    return members.map((member) => ({
      value: String(member.id),
      label: `${memberName(member)} - ${member.email}`
    }));
  }, [members]);

  const metrics = useMemo(() => {
    const pendingLoans = loans.filter((loan) => loan.status === "pending").length;
    const activeLoans = loans.filter((loan) => ["approved", "disbursed", "active"].includes(loan.status));
    const activePrincipal = activeLoans.reduce(
      (sum, loan) => sum + Number.parseFloat(loan.principal_amount || "0"),
      0
    );
    const pendingKyc = kyc.filter((entry) => entry.status === "submitted").length;
    const walletTotal =
      overview?.wallet_total ||
      members.reduce((sum, member) => sum + Number.parseFloat(member.wallet_balance || "0"), 0);

    return {
      pendingLoans,
      activePrincipal,
      pendingKyc,
      walletTotal
    };
  }, [kyc, loans, members, overview]);

  const loadMemberDetail = async (memberId) => {
    setMemberModalOpen(true);
    setMemberDetailLoading(true);
    setSelectedMember(null);
    setError("");
    try {
      const { data } = await apiClient.get(`/admin/members/${memberId}`);
      setSelectedMember(data);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not load member profile.");
      setMemberModalOpen(false);
    } finally {
      setMemberDetailLoading(false);
    }
  };

  const closeMemberDetail = () => {
    setMemberModalOpen(false);
    setSelectedMember(null);
  };

  const refreshAfterAction = async (message) => {
    setStatus(message);
    await loadData();
  };

  const updateMemberStatus = async (member, nextStatus) => {
    setSavingAction(true);
    setStatus("");
    setError("");
    try {
      await apiClient.patch(`/admin/members/${member.id}/status`, { status: nextStatus });
      await refreshAfterAction(`${memberName(member)} marked ${nextStatus}.`);
      if (selectedMember?.member?.id === member.id) {
        await loadMemberDetail(member.id);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update member status.");
    } finally {
      setSavingAction(false);
    }
  };

  const submitWalletAction = async (event) => {
    event.preventDefault();
    if (!walletForm.member_id || !walletForm.amount) return;
    setSavingAction(true);
    setStatus("");
    setError("");
    const actionPath = walletForm.type === "withdrawal" ? "withdrawal" : "top-up";
    try {
      await apiClient.post(`/admin/members/${walletForm.member_id}/wallet/${actionPath}`, {
        amount: walletForm.amount,
        note: walletForm.note
      });
      setWalletForm(emptyWalletForm);
      await refreshAfterAction(`Wallet ${actionPath.replace("-", " ")} completed.`);
    } catch (err) {
      setError(err?.response?.data?.message || "Wallet action failed.");
    } finally {
      setSavingAction(false);
    }
  };

  const updateLoanStatus = async (loan, statusValue) => {
    setSavingAction(true);
    setStatus("");
    setError("");
    try {
      await apiClient.patch(`/admin/loans/${loan.id}/status`, { status: statusValue });
      await refreshAfterAction(`Loan ${loan.id} updated to ${statusValue}.`);
    } catch (err) {
      setError(err?.response?.data?.message || "Could not update loan.");
    } finally {
      setSavingAction(false);
    }
  };

  const reviewKyc = async (submission, action) => {
    setSavingAction(true);
    setStatus("");
    setError("");
    try {
      await apiClient.post(`/admin/kyc/${submission.id}/${action}`, { review_note: "" });
      await refreshAfterAction(`KYC ${action.replace("-", " ")} recorded.`);
    } catch (err) {
      setError(err?.response?.data?.message || "KYC review failed.");
    } finally {
      setSavingAction(false);
    }
  };

  const submitProductAction = async (endpoint, payload, message) => {
    setSavingAction(true);
    setStatus("");
    setError("");
    try {
      await apiClient.post(endpoint, payload);
      await refreshAfterAction(message);
    } catch (err) {
      setError(err?.response?.data?.message || "Product action failed.");
    } finally {
      setSavingAction(false);
    }
  };

  const allocateDividendRun = async (run) => {
    setSavingAction(true);
    setStatus("");
    setError("");
    try {
      await apiClient.post(`/admin/dividend-runs/${run.id}/allocate`);
      await refreshAfterAction(`${run.name} allocated.`);
    } catch (err) {
      setError(err?.response?.data?.message || "Dividend allocation failed.");
    } finally {
      setSavingAction(false);
    }
  };

  return (
    <div className="space-y-6 fade-up">
      <section className="rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <PageHeader
            title="Administrative Hub"
            description="Centralized cooperative operations, wallet controls, KYC, savings, loans, and audit oversight."
          />
          <span className="rounded-xl bg-[rgba(129,178,154,0.2)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-accent">
            {loading ? "Syncing" : "Live operations"}
          </span>
        </div>
      </section>

      {status && <p className="rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{status}</p>}
      {error && <p className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p>}

      <section className="grid gap-4 md:grid-cols-4">
        <StatCard label="Pooled Wallets" value={formatMoney(metrics.walletTotal)} subtext="Member wallet exposure" />
        <StatCard
          label="Contributions"
          value={formatMoney(overview?.contributions_total)}
          subtext="Recorded cooperative inflow"
        />
        <StatCard label="Pending KYC" value={`${metrics.pendingKyc}`} subtext="Awaiting review" />
        <StatCard label="Loan Pipeline" value={`${metrics.pendingLoans}`} subtext={formatMoney(metrics.activePrincipal)} />
      </section>

      <nav className="flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={`whitespace-nowrap rounded-xl px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] transition ${
              activeSection === section.id
                ? "bg-accent text-white"
                : "text-slate-600 hover:bg-slate-50"
            }`}
          >
            {section.label}
          </button>
        ))}
      </nav>

      {activeSection === "overview" && (
        <OverviewSection
          overview={overview}
          members={members}
          kyc={kyc}
          loans={loans}
          auditLogs={auditLogs}
        />
      )}

      {activeSection === "members" && (
        <MembersSection
          members={members}
          kyc={kyc}
          onViewMember={loadMemberDetail}
          onStatusChange={updateMemberStatus}
          onKycAction={reviewKyc}
          savingAction={savingAction}
        />
      )}

      {activeSection === "products" && (
        <ProductStudioSection
          memberOptions={memberOptions}
          thriftPlans={thriftPlans}
          loanProducts={loanProducts}
          agentCollections={agentCollections}
          investmentPortfolios={investmentPortfolios}
          dividendRuns={dividendRuns}
          ajoCircles={ajoCircles}
          onSubmitProduct={submitProductAction}
          onAllocateDividend={allocateDividendRun}
          savingAction={savingAction}
        />
      )}

      {activeSection === "savings" && (
        <SavingsSection
          savings={savings}
          contributions={contributions}
          transactions={transactions}
          walletForm={walletForm}
          setWalletForm={setWalletForm}
          memberOptions={memberOptions}
          onWalletSubmit={submitWalletAction}
          savingAction={savingAction}
        />
      )}

      {activeSection === "loans" && (
        <LoansSection loans={loans} onLoanStatus={updateLoanStatus} savingAction={savingAction} />
      )}

      {activeSection === "accounting" && (
        <AccountingSection
          overview={overview}
          transactions={transactions}
          contributions={contributions}
          auditLogs={auditLogs}
        />
      )}

      {activeSection === "communications" && <CommunicationsSection />}

      <MemberProfileModal
        open={memberModalOpen}
        detail={selectedMember}
        loading={memberDetailLoading}
        onClose={closeMemberDetail}
        onStatusChange={updateMemberStatus}
        savingAction={savingAction}
      />
    </div>
  );
}

function OverviewSection({ overview, members, kyc, loans, auditLogs }) {
  const suspendedMembers = members.filter((member) => member.status !== "active");
  const pendingKyc = kyc.filter((entry) => entry.status === "submitted");
  const activeLoans = loans.filter((loan) => ["approved", "disbursed", "active"].includes(loan.status));

  return (
    <section className="grid gap-6 lg:grid-cols-[1.1fr_0.9fr]">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xl font-semibold text-accent">Operations Snapshot</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <MetricLine label="Members" value={overview?.members_total || members.length} />
          <MetricLine label="Verified" value={overview?.verified_members || 0} />
          <MetricLine label="Suspended / inactive" value={suspendedMembers.length} />
          <MetricLine label="Pending KYC" value={pendingKyc.length} />
          <MetricLine label="Savings current" value={formatMoney(overview?.savings_current_total)} />
          <MetricLine label="Savings target" value={formatMoney(overview?.savings_target_total)} />
          <MetricLine label="Active loans" value={activeLoans.length} />
          <MetricLine label="Wallet transactions" value={overview?.wallet_transactions_total || 0} />
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xl font-semibold text-accent">Recent Audit Trail</h2>
        <div className="mt-5 space-y-3">
          {auditLogs.slice(0, 6).map((entry) => (
            <div key={entry.id} className="rounded-xl bg-slate-50 px-4 py-3">
              <p className="text-sm font-semibold text-ink">{labelize(entry.action)}</p>
              <p className="text-xs text-slate-500">
                {entry.actor?.email || "System"} · {formatDate(entry.created_at)}
              </p>
            </div>
          ))}
          {!auditLogs.length && <p className="text-sm text-slate-500">No administrative activity logged yet.</p>}
        </div>
      </div>
    </section>
  );
}

function ProductStudioSection({
  memberOptions,
  thriftPlans,
  loanProducts,
  agentCollections,
  investmentPortfolios,
  dividendRuns,
  ajoCircles,
  onSubmitProduct,
  onAllocateDividend,
  savingAction
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [thriftForm, setThriftForm] = useState({
    member_id: "",
    name: "Daily Ajo",
    amount: "",
    frequency: "daily",
    collection_method: "agent_cash",
    next_due_date: today
  });
  const [collectionForm, setCollectionForm] = useState({
    member_id: "",
    amount: "",
    channel: "cash",
    note: ""
  });
  const [loanProductForm, setLoanProductForm] = useState({
    name: "Emergency Loan",
    product_type: "emergency",
    max_amount: "",
    interest_rate: "",
    tenure_months: "3"
  });
  const [investmentForm, setInvestmentForm] = useState({
    name: "Treasury Bills Pool",
    asset_class: "treasury_bills",
    target_amount: "",
    expected_return_rate: "",
    description: ""
  });
  const [dividendForm, setDividendForm] = useState({
    name: "Annual dividend",
    period_start: `${new Date().getFullYear()}-01-01`,
    period_end: `${new Date().getFullYear()}-12-31`,
    profit_pool: "",
    basis: "wallet_balance"
  });
  const [ajoForm, setAjoForm] = useState({
    name: "Market Women Daily Ajo",
    contribution_amount: "",
    frequency: "daily",
    capacity: "12"
  });

  const update = (setter) => (event) => {
    const { name, value } = event.target;
    setter((prev) => ({ ...prev, [name]: value }));
  };

  return (
    <section className="space-y-6">
      <div className="grid gap-4 md:grid-cols-3 xl:grid-cols-6">
        <MetricLine label="Thrift plans" value={thriftPlans.length} />
        <MetricLine label="Loan Products" value={loanProducts.length} />
        <MetricLine label="Agent Cash" value={formatMoney(sumAmount(agentCollections, "amount"))} />
        <MetricLine label="Investments" value={investmentPortfolios.length} />
        <MetricLine label="Dividend Runs" value={dividendRuns.length} />
        <MetricLine label="Ajo Circles" value={ajoCircles.length} />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <ProductForm title="Create Thrift Plan" onSubmit={() => onSubmitProduct("/admin/thrift-plans", thriftForm, "Thrift plan created.")} savingAction={savingAction}>
          <SelectField name="member_id" value={thriftForm.member_id} onChange={update(setThriftForm)} options={memberOptions} placeholder="Select member" />
          <InputField name="name" value={thriftForm.name} onChange={update(setThriftForm)} placeholder="Plan name" />
          <InputField name="amount" type="number" value={thriftForm.amount} onChange={update(setThriftForm)} placeholder="Contribution amount" />
          <SelectField
            name="frequency"
            value={thriftForm.frequency}
            onChange={update(setThriftForm)}
            options={["daily", "weekly", "monthly"].map((item) => ({ value: item, label: labelize(item) }))}
          />
          <SelectField
            name="collection_method"
            value={thriftForm.collection_method}
            onChange={update(setThriftForm)}
            options={[
              { value: "wallet", label: "Wallet debit" },
              { value: "bank_transfer", label: "Bank transfer" },
              { value: "agent_cash", label: "Agent cash" }
            ]}
          />
          <InputField name="next_due_date" type="date" value={thriftForm.next_due_date} onChange={update(setThriftForm)} />
        </ProductForm>

        <ProductForm title="Record Agent Cash Collection" onSubmit={() => onSubmitProduct("/admin/agent-collections", collectionForm, "Agent collection recorded.")} savingAction={savingAction}>
          <SelectField name="member_id" value={collectionForm.member_id} onChange={update(setCollectionForm)} options={memberOptions} placeholder="Select member" />
          <InputField name="amount" type="number" value={collectionForm.amount} onChange={update(setCollectionForm)} placeholder="Cash amount" />
          <SelectField
            name="channel"
            value={collectionForm.channel}
            onChange={update(setCollectionForm)}
            options={[
              { value: "cash", label: "Cash" },
              { value: "pos", label: "POS" },
              { value: "transfer", label: "Transfer" }
            ]}
          />
          <InputField name="note" value={collectionForm.note} onChange={update(setCollectionForm)} placeholder="Receipt note" />
        </ProductForm>

        <ProductForm title="Configure Loan Product" onSubmit={() => onSubmitProduct("/admin/loan-products", loanProductForm, "Loan product created.")} savingAction={savingAction}>
          <InputField name="name" value={loanProductForm.name} onChange={update(setLoanProductForm)} placeholder="Product name" />
          <SelectField
            name="product_type"
            value={loanProductForm.product_type}
            onChange={update(setLoanProductForm)}
            options={["emergency", "business_capital", "asset_finance", "agric_finance"].map((item) => ({ value: item, label: labelize(item) }))}
          />
          <InputField name="max_amount" type="number" value={loanProductForm.max_amount} onChange={update(setLoanProductForm)} placeholder="Maximum amount" />
          <InputField name="interest_rate" type="number" value={loanProductForm.interest_rate} onChange={update(setLoanProductForm)} placeholder="Interest rate" />
          <InputField name="tenure_months" type="number" value={loanProductForm.tenure_months} onChange={update(setLoanProductForm)} placeholder="Tenure months" />
        </ProductForm>

        <ProductForm title="Open Investment Portfolio" onSubmit={() => onSubmitProduct("/admin/investment-portfolios", investmentForm, "Investment portfolio opened.")} savingAction={savingAction}>
          <InputField name="name" value={investmentForm.name} onChange={update(setInvestmentForm)} placeholder="Portfolio name" />
          <SelectField
            name="asset_class"
            value={investmentForm.asset_class}
            onChange={update(setInvestmentForm)}
            options={["real_estate", "treasury_bills", "agriculture", "cooperative"].map((item) => ({ value: item, label: labelize(item) }))}
          />
          <InputField name="target_amount" type="number" value={investmentForm.target_amount} onChange={update(setInvestmentForm)} placeholder="Target raise" />
          <InputField name="expected_return_rate" type="number" value={investmentForm.expected_return_rate} onChange={update(setInvestmentForm)} placeholder="Expected return %" />
          <InputField name="description" value={investmentForm.description} onChange={update(setInvestmentForm)} placeholder="Investment description" />
        </ProductForm>

        <ProductForm title="Create Dividend Run" onSubmit={() => onSubmitProduct("/admin/dividend-runs", dividendForm, "Dividend run created.")} savingAction={savingAction}>
          <InputField name="name" value={dividendForm.name} onChange={update(setDividendForm)} placeholder="Run name" />
          <InputField name="period_start" type="date" value={dividendForm.period_start} onChange={update(setDividendForm)} />
          <InputField name="period_end" type="date" value={dividendForm.period_end} onChange={update(setDividendForm)} />
          <InputField name="profit_pool" type="number" value={dividendForm.profit_pool} onChange={update(setDividendForm)} placeholder="Profit pool" />
          <SelectField
            name="basis"
            value={dividendForm.basis}
            onChange={update(setDividendForm)}
            options={[
              { value: "wallet_balance", label: "Wallet balance weighted" },
              { value: "equal", label: "Equal allocation" }
            ]}
          />
        </ProductForm>

        <ProductForm title="Open Ajo Circle" onSubmit={() => onSubmitProduct("/admin/ajo-circles", ajoForm, "Ajo circle opened.")} savingAction={savingAction}>
          <InputField name="name" value={ajoForm.name} onChange={update(setAjoForm)} placeholder="Circle name" />
          <InputField name="contribution_amount" type="number" value={ajoForm.contribution_amount} onChange={update(setAjoForm)} placeholder="Contribution amount" />
          <SelectField
            name="frequency"
            value={ajoForm.frequency}
            onChange={update(setAjoForm)}
            options={["daily", "weekly", "monthly"].map((item) => ({ value: item, label: labelize(item) }))}
          />
          <InputField name="capacity" type="number" value={ajoForm.capacity} onChange={update(setAjoForm)} placeholder="Member capacity" />
        </ProductForm>
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <LedgerTable
          title="Loan Product Shelf"
          rows={loanProducts}
          columns={[
            ["Product", (row) => row.name],
            ["Type", (row) => labelize(row.product_type)],
            ["Limit", (row) => formatMoney(row.max_amount)],
            ["Rate", (row) => `${row.interest_rate}%`]
          ]}
        />
        <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-xl font-semibold text-accent">Dividend Runs</h2>
            <span className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">{dividendRuns.length}</span>
          </div>
          <div className="mt-5 space-y-3">
            {dividendRuns.slice(0, 8).map((run) => (
              <div key={run.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm">
                <div>
                  <p className="font-semibold text-ink">{run.name}</p>
                  <p className="text-xs text-slate-500">{formatMoney(run.profit_pool)} · {labelize(run.status)}</p>
                </div>
                {run.status === "draft" && (
                  <button
                    type="button"
                    disabled={savingAction}
                    onClick={() => onAllocateDividend(run)}
                    className="rounded-xl bg-accent px-3 py-2 text-xs font-semibold text-white"
                  >
                    Allocate
                  </button>
                )}
              </div>
            ))}
            {!dividendRuns.length && <p className="text-sm text-slate-500">No dividend runs created.</p>}
          </div>
        </section>
      </div>
    </section>
  );
}

function ProductForm({ title, children, onSubmit, savingAction }) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
      className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
    >
      <h2 className="text-xl font-semibold text-accent">{title}</h2>
      <div className="mt-5 grid gap-3 sm:grid-cols-2">{children}</div>
      <button
        type="submit"
        disabled={savingAction}
        className="mt-4 w-full rounded-xl bg-accent px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white disabled:opacity-50"
      >
        Save
      </button>
    </form>
  );
}

function InputField({ name, value, onChange, placeholder, type = "text" }) {
  return (
    <input
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="rounded-xl border border-slate-200 px-3 py-3 text-sm"
      required={name !== "description" && name !== "note"}
    />
  );
}

function SelectField({ name, value, onChange, options, placeholder }) {
  return (
    <select
      name={name}
      value={value}
      onChange={onChange}
      className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm"
      required
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function MembersSection({
  members,
  kyc,
  onViewMember,
  onStatusChange,
  onKycAction,
  savingAction
}) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [kycFilter, setKycFilter] = useState("all");
  const pendingKyc = kyc.filter((entry) => entry.status === "submitted");
  const verifiedMembers = members.filter((member) => member.is_email_verified).length;
  const activeMembers = members.filter((member) => member.status === "active").length;
  const suspendedMembers = members.filter((member) => member.status !== "active").length;
  const filteredMembers = members.filter((member) => {
    const latestKyc = latestKycForMember(member.id, kyc);
    const haystack = `${memberName(member)} ${member.email} ${member.phone || ""}`.toLowerCase();
    const matchesQuery = !query.trim() || haystack.includes(query.trim().toLowerCase());
    const matchesStatus = statusFilter === "all" || member.status === statusFilter;
    const matchesKyc =
      kycFilter === "all" ||
      (kycFilter === "none" ? !latestKyc : latestKyc?.status === kycFilter);
    return matchesQuery && matchesStatus && matchesKyc;
  });

  return (
    <section className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-accent">Member Management</h2>
            <p className="text-sm text-slate-600">{members.length} accounts in the cooperative</p>
          </div>
          <span className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
            {filteredMembers.length} shown
          </span>
        </div>

        <div className="mt-5 grid gap-3 md:grid-cols-4">
          <MetricLine label="Active" value={activeMembers} />
          <MetricLine label="Suspended / inactive" value={suspendedMembers} />
          <MetricLine label="Email verified" value={verifiedMembers} />
          <MetricLine label="Pending KYC" value={pendingKyc.length} />
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-[1fr_180px_190px]">
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search members by name, email, or phone"
            className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[rgba(129,178,154,0.35)]"
          />
          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700"
          >
            <option value="all">All statuses</option>
            {memberStatuses.map((status) => (
              <option key={status} value={status}>
                {labelize(status)}
              </option>
            ))}
          </select>
          <select
            value={kycFilter}
            onChange={(event) => setKycFilter(event.target.value)}
            className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700"
          >
            <option value="all">All KYC</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="needs_more_info">Needs more info</option>
            <option value="rejected">Rejected</option>
            <option value="none">No KYC</option>
          </select>
        </div>

        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[980px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
                <th className="py-3 pr-4">Member</th>
                <th className="py-3 pr-4">Contact</th>
                <th className="py-3 pr-4">Role</th>
                <th className="py-3 pr-4">Account</th>
                <th className="py-3 pr-4">KYC</th>
                <th className="py-3 pr-4">Wallet</th>
                <th className="py-3 pr-4">Controls</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredMembers.map((member) => {
                const latestKyc = latestKycForMember(member.id, kyc);
                return (
                  <tr key={member.id}>
                    <td className="py-3 pr-4">
                      <p className="font-semibold text-ink">{memberName(member)}</p>
                      <p className="text-xs text-slate-500">Member #{member.id}</p>
                    </td>
                    <td className="py-3 pr-4">
                      <p className="text-sm text-slate-700">{member.email}</p>
                      <p className="text-xs text-slate-500">{member.phone || "No phone on file"}</p>
                    </td>
                    <td className="py-3 pr-4 text-xs font-semibold uppercase tracking-[0.14em] text-slate-500">
                      {labelize(member.role)}
                    </td>
                    <td className="py-3 pr-4">
                      <div className="space-y-2">
                        <StatusBadge value={member.status} />
                        <p className="text-xs text-slate-500">
                          {member.is_email_verified ? "Email verified" : "Email not verified"}
                        </p>
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      {latestKyc ? <StatusBadge value={latestKyc.status} /> : <span className="text-xs text-slate-500">No file</span>}
                    </td>
                    <td className="py-3 pr-4 font-semibold text-ink">{formatMoney(member.wallet_balance)}</td>
                    <td className="py-3 pr-4">
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => onViewMember(member.id)}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                        >
                          360 View
                        </button>
                        <select
                          value={member.status}
                          disabled={savingAction}
                          onChange={(event) => onStatusChange(member, event.target.value)}
                          className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                        >
                          {memberStatuses.map((status) => (
                            <option key={status} value={status}>
                              {labelize(status)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!filteredMembers.length && <p className="py-6 text-sm text-slate-500">No members match the current filters.</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-accent">KYC Verification Queue</h2>
            <p className="text-sm text-slate-600">Review submitted documents and open the full member profile when context is needed.</p>
          </div>
          <span className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
            {pendingKyc.length} pending
          </span>
        </div>
        <div className="mt-5 divide-y divide-slate-100">
          {pendingKyc.map((submission) => (
            <div key={submission.id} className="flex flex-wrap items-start justify-between gap-4 py-4">
              <div>
                <p className="text-sm font-semibold text-ink">{memberName(submission.member)}</p>
                <p className="text-xs text-slate-500">{submission.member?.email || submission.document_filename}</p>
                <p className="mt-2 text-xs uppercase tracking-[0.16em] text-slate-500">
                  {submission.document_type}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onViewMember(submission.member_id)}
                  className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
                >
                  Profile
                </button>
                {submission.document_url && (
                  <a
                    href={submission.document_url}
                    target="_blank"
                    rel="noreferrer"
                    className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
                  >
                    Document
                  </a>
                )}
                <button
                  type="button"
                  disabled={savingAction}
                  onClick={() => onKycAction(submission, "approve")}
                  className="rounded-xl bg-mint/30 px-3 py-2 text-xs font-semibold text-accent"
                >
                  Approve
                </button>
                <button
                  type="button"
                  disabled={savingAction}
                  onClick={() => onKycAction(submission, "request-documents")}
                  className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700"
                >
                  More ID
                </button>
                <button
                  type="button"
                  disabled={savingAction}
                  onClick={() => onKycAction(submission, "reject")}
                  className="rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700"
                >
                  Reject
                </button>
              </div>
            </div>
          ))}
          {!pendingKyc.length && <p className="py-6 text-sm text-slate-500">No KYC submissions are pending.</p>}
        </div>
      </div>
    </section>
  );
}

function MemberProfileModal({ open, detail, loading, onClose, onStatusChange, savingAction }) {
  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) return null;

  const member = detail?.member;
  const contributions = detail?.contributions || [];
  const savings = detail?.savings || [];
  const loans = detail?.loans || [];
  const kyc = detail?.kyc || [];
  const thriftPlans = detail?.thrift_plans || [];
  const agentCollections = detail?.agent_collections || [];
  const dividendAllocations = detail?.dividend_allocations || [];
  const ajoCircles = detail?.ajo_circles || [];
  const walletTransactions = detail?.wallet_transactions || [];
  const savingsCurrent = sumAmount(savings, "current_amount");
  const savingsTarget = sumAmount(savings, "target_amount");
  const contributionsTotal = sumAmount(contributions, "contribution_amount");
  const activeLoans = loans.filter((loan) => ["approved", "disbursed", "active"].includes(loan.status));
  const activeLoanPrincipal = sumAmount(activeLoans, "principal_amount");
  const profileEntries = Object.entries(member?.profile_data || {}).filter(([, value]) => value);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-950/50 px-3 py-3 sm:px-4 sm:py-5">
      <button
        type="button"
        aria-label="Close member profile"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <section
        role="dialog"
        aria-modal="true"
        className="relative z-10 flex max-h-[calc(100vh-1.5rem)] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:max-h-[calc(100vh-2.5rem)]"
      >
        {loading && !member ? (
          <div className="flex min-h-[360px] items-center justify-center p-5 text-sm text-slate-500 md:p-6">
            Loading member profile...
          </div>
        ) : !member ? (
          <div className="flex min-h-[360px] items-center justify-center p-5 text-sm text-slate-500 md:p-6">
            Member profile could not be loaded.
          </div>
        ) : (
          <div className="overflow-y-auto p-5 md:p-6">
            <div className="sticky top-0 z-20 -mx-5 -mt-5 flex flex-wrap items-start justify-between gap-4 border-b border-slate-100 bg-white/95 px-5 pb-5 pt-5 backdrop-blur md:-mx-6 md:-mt-6 md:px-6 md:pt-6">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Member 360 Profile</p>
                <h2 className="mt-2 text-2xl font-semibold text-accent">{memberName(member)}</h2>
                <p className="text-sm text-slate-600">{member.email}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <StatusBadge value={member.status} />
                  <StatusBadge value={kyc[0]?.status || "no_kyc"} />
                  <span className="rounded-xl bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-600">
                    {labelize(member.role)}
                  </span>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={member.status}
                  disabled={savingAction}
                  onChange={(event) => onStatusChange(member, event.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                >
                  {memberStatuses.map((status) => (
                    <option key={status} value={status}>
                      {labelize(status)}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl bg-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-700"
                >
                  Close
                </button>
              </div>
            </div>

            <div className="mt-6 grid gap-3 md:grid-cols-3 xl:grid-cols-6">
              <MetricLine label="Wallet" value={formatMoney(member.wallet_balance)} />
              <MetricLine label="Contributions" value={formatMoney(contributionsTotal)} />
              <MetricLine label="Savings current" value={formatMoney(savingsCurrent)} />
              <MetricLine label="Savings target" value={formatMoney(savingsTarget)} />
              <MetricLine label="Active loans" value={formatMoney(activeLoanPrincipal)} />
              <MetricLine label="KYC files" value={kyc.length} />
            </div>

            <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
              <section className="rounded-2xl border border-slate-200 p-5">
                <h3 className="text-lg font-semibold text-accent">Profile & Compliance</h3>
                <div className="mt-4 grid gap-3 sm:grid-cols-2">
                  <DetailLine label="Member ID" value={`#${member.id}`} />
                  <DetailLine label="Joined" value={formatDate(member.join_date || member.created_at)} />
                  <DetailLine label="Phone" value={member.phone || "Not provided"} />
                  <DetailLine label="Email status" value={member.is_email_verified ? "Verified" : "Not verified"} />
                </div>
                {profileEntries.length > 0 && (
                  <div className="mt-4 grid gap-3 sm:grid-cols-2">
                    {profileEntries.map(([key, value]) => (
                      <DetailLine key={key} label={labelize(key)} value={String(value)} />
                    ))}
                  </div>
                )}

                <div className="mt-5">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">KYC documents</h4>
                  <div className="mt-3 space-y-3">
                    {kyc.map((entry) => (
                      <div key={entry.id} className="rounded-xl bg-slate-50 px-4 py-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <p className="text-sm font-semibold text-ink">{entry.document_type}</p>
                            <p className="text-xs text-slate-500">{entry.document_filename}</p>
                          </div>
                          <StatusBadge value={entry.status} />
                        </div>
                        {entry.review_note && <p className="mt-2 text-xs text-slate-600">{entry.review_note}</p>}
                        {entry.document_url && (
                          <a
                            href={entry.document_url}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700"
                          >
                            Open document
                          </a>
                        )}
                      </div>
                    ))}
                    {!kyc.length && <p className="text-sm text-slate-500">No KYC document has been uploaded.</p>}
                  </div>
                </div>
              </section>

              <section className="space-y-5">
                <MemberModalList
                  title="Thrift Plans"
                  rows={thriftPlans}
                  empty="No thrift plan found."
                  render={(entry) => (
                    <>
                      <span>{entry.name}</span>
                      <span className="font-semibold text-ink">
                        {formatMoney(entry.amount)} · {labelize(entry.frequency)}
                      </span>
                    </>
                  )}
                />
                <MemberModalList
                  title="Contributions"
                  rows={contributions}
                  empty="No contributions recorded."
                  render={(entry) => (
                    <>
                      <span>{entry.item_name}</span>
                      <span className="font-semibold text-ink">{formatMoney(entry.contribution_amount)}</span>
                    </>
                  )}
                />
                <MemberModalList
                  title="Savings Goals"
                  rows={savings}
                  empty="No target savings found."
                  render={(entry) => (
                    <>
                      <span>{entry.name}</span>
                      <span className="font-semibold text-ink">
                        {formatMoney(entry.current_amount)} / {formatMoney(entry.target_amount)}
                      </span>
                    </>
                  )}
                />
                <MemberModalList
                  title="Loans"
                  rows={loans}
                  empty="No loan record found."
                  render={(entry) => (
                    <>
                      <span>{entry.purpose || `Loan #${entry.id}`}</span>
                      <span className="flex items-center gap-2">
                        <span className="font-semibold text-ink">{formatMoney(entry.principal_amount)}</span>
                        <StatusBadge value={entry.status} />
                      </span>
                    </>
                  )}
                />
                <MemberModalList
                  title="Ajo Circles"
                  rows={ajoCircles}
                  empty="No Ajo circle membership."
                  render={(entry) => (
                    <>
                      <span>{entry.name}</span>
                      <span className="font-semibold text-ink">
                        Position {entry.my_payout_position || "N/A"}
                      </span>
                    </>
                  )}
                />
                <MemberModalList
                  title="Dividends"
                  rows={dividendAllocations}
                  empty="No dividend allocation yet."
                  render={(entry) => (
                    <>
                      <span>{entry.run?.name || `Run #${entry.run_id}`}</span>
                      <span className="font-semibold text-ink">{formatMoney(entry.amount)}</span>
                    </>
                  )}
                />
                <MemberModalList
                  title="Agent Collections"
                  rows={agentCollections}
                  empty="No agent cash collection."
                  render={(entry) => (
                    <>
                      <span>{entry.reference}</span>
                      <span className="font-semibold text-ink">{formatMoney(entry.amount)}</span>
                    </>
                  )}
                />
              </section>
            </div>

            <section className="rounded-2xl border border-slate-200 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-lg font-semibold text-accent">Wallet Activity</h3>
                <span className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
                  {walletTransactions.length} entries
                </span>
              </div>
              <div className="mt-4 overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
                      <th className="py-3 pr-4">Date</th>
                      <th className="py-3 pr-4">Type</th>
                      <th className="py-3 pr-4">Reference</th>
                      <th className="py-3 pr-4">Amount</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {walletTransactions.slice(0, 12).map((entry) => (
                      <tr key={entry.id}>
                        <td className="py-3 pr-4 text-slate-600">{formatDate(entry.created_at)}</td>
                        <td className="py-3 pr-4 text-slate-600">{labelize(entry.transaction_type)}</td>
                        <td className="py-3 pr-4 text-slate-600">{entry.reference}</td>
                        <td className="py-3 pr-4 font-semibold text-ink">{formatMoney(entry.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {!walletTransactions.length && <p className="py-6 text-sm text-slate-500">No wallet activity.</p>}
              </div>
            </section>
          </div>
        )}
      </section>
    </div>
  );
}

function DetailLine({ label, value }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-ink">{value}</p>
    </div>
  );
}

function MemberModalList({ title, rows, empty, render }) {
  return (
    <div className="rounded-2xl border border-slate-200 p-5">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-lg font-semibold text-accent">{title}</h3>
        <span className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
          {rows.length}
        </span>
      </div>
      <div className="mt-4 space-y-3">
        {rows.slice(0, 5).map((entry) => (
          <div key={`${title}-${entry.id}`} className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600">
            {render(entry)}
          </div>
        ))}
        {!rows.length && <p className="text-sm text-slate-500">{empty}</p>}
      </div>
    </div>
  );
}

function SavingsSection({
  savings,
  contributions,
  transactions,
  walletForm,
  setWalletForm,
  memberOptions,
  onWalletSubmit,
  savingAction
}) {
  return (
    <section className="space-y-6">
      <div className="grid gap-6 lg:grid-cols-[0.8fr_1.2fr]">
        <form onSubmit={onWalletSubmit} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h2 className="text-xl font-semibold text-accent">Wallet Reconciliation</h2>
          <div className="mt-5 space-y-4">
            <select
              value={walletForm.member_id}
              onChange={(event) => setWalletForm((prev) => ({ ...prev, member_id: event.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm"
              required
            >
              <option value="">Select member</option>
              {memberOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <div className="grid gap-3 sm:grid-cols-2">
              <select
                value={walletForm.type}
                onChange={(event) => setWalletForm((prev) => ({ ...prev, type: event.target.value }))}
                className="rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm"
              >
                <option value="top-up">Top-up</option>
                <option value="withdrawal">Withdrawal</option>
              </select>
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={walletForm.amount}
                onChange={(event) => setWalletForm((prev) => ({ ...prev, amount: event.target.value }))}
                placeholder="Amount"
                className="rounded-xl border border-slate-200 px-3 py-3 text-sm"
                required
              />
            </div>
            <input
              value={walletForm.note}
              onChange={(event) => setWalletForm((prev) => ({ ...prev, note: event.target.value }))}
              placeholder="Reconciliation note"
              className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm"
            />
            <button
              type="submit"
              disabled={savingAction}
              className="w-full rounded-xl bg-accent px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-white disabled:opacity-50"
            >
              Record wallet action
            </button>
          </div>
        </form>

        <LedgerTable
          title="Contribution Ledger"
          rows={contributions}
          columns={[
            ["Member", (row) => memberName(row.member)],
            ["Item", (row) => row.item_name],
            ["Amount", (row) => formatMoney(row.contribution_amount)],
            ["Date", (row) => formatDate(row.created_at)]
          ]}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <LedgerTable
          title="Savings Tracking"
          rows={savings}
          columns={[
            ["Member", (row) => memberName(row.member)],
            ["Goal", (row) => row.name],
            ["Current", (row) => formatMoney(row.current_amount)],
            ["Target", (row) => formatMoney(row.target_amount)],
            ["Status", (row) => <StatusBadge value={row.status} />]
          ]}
        />
        <LedgerTable
          title="Wallet Transactions"
          rows={transactions}
          columns={[
            ["Member", (row) => memberName(row.member)],
            ["Type", (row) => labelize(row.transaction_type)],
            ["Amount", (row) => formatMoney(row.amount)],
            ["Date", (row) => formatDate(row.created_at)]
          ]}
        />
      </div>
    </section>
  );
}

function LoansSection({ loans, onLoanStatus, savingAction }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-accent">Loan Administration</h2>
          <p className="text-sm text-slate-600">{loans.length} applications and active facilities</p>
        </div>
      </div>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[880px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
              <th className="py-3 pr-4">Member</th>
              <th className="py-3 pr-4">Principal</th>
              <th className="py-3 pr-4">Rate</th>
              <th className="py-3 pr-4">Tenure</th>
              <th className="py-3 pr-4">Purpose</th>
              <th className="py-3 pr-4">Status</th>
              <th className="py-3 pr-4">Workflow</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loans.map((loan) => (
              <tr key={loan.id}>
                <td className="py-3 pr-4">
                  <p className="font-semibold text-ink">{memberName(loan.member)}</p>
                  <p className="text-xs text-slate-500">Loan #{loan.id}</p>
                </td>
                <td className="py-3 pr-4 font-semibold text-ink">{formatMoney(loan.principal_amount)}</td>
                <td className="py-3 pr-4">{loan.interest_rate}%</td>
                <td className="py-3 pr-4">{loan.tenure_months} months</td>
                <td className="py-3 pr-4 text-slate-600">{loan.purpose || "Not specified"}</td>
                <td className="py-3 pr-4"><StatusBadge value={loan.status} /></td>
                <td className="py-3 pr-4">
                  <select
                    value={loan.status}
                    disabled={savingAction}
                    onChange={(event) => onLoanStatus(loan, event.target.value)}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700"
                  >
                    {loanStatuses.map((status) => (
                      <option key={status} value={status}>
                        {labelize(status)}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loans.length && <p className="py-6 text-sm text-slate-500">No loan records found.</p>}
      </div>
    </section>
  );
}

function AccountingSection({ overview, transactions, contributions, auditLogs }) {
  return (
    <section className="space-y-6">
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total Wallets" value={formatMoney(overview?.wallet_total)} subtext="Balance sheet liability" />
        <StatCard label="Savings Current" value={formatMoney(overview?.savings_current_total)} subtext="Savings ledger" />
        <StatCard label="Savings Target" value={formatMoney(overview?.savings_target_total)} subtext="Member goals" />
        <StatCard label="Contribution Income" value={formatMoney(overview?.contributions_total)} subtext="Recorded inflow" />
      </div>
      <div className="grid gap-6 xl:grid-cols-2">
        <LedgerTable
          title="General Ledger"
          rows={transactions}
          columns={[
            ["Reference", (row) => row.reference],
            ["Type", (row) => labelize(row.transaction_type)],
            ["Amount", (row) => formatMoney(row.amount)],
            ["Member", (row) => memberName(row.member)]
          ]}
        />
        <LedgerTable
          title="Income Entries"
          rows={contributions}
          columns={[
            ["Member", (row) => memberName(row.member)],
            ["Item", (row) => row.item_name],
            ["Amount", (row) => formatMoney(row.contribution_amount)],
            ["Date", (row) => formatDate(row.created_at)]
          ]}
        />
      </div>
      <LedgerTable
        title="Audit Trail"
        rows={auditLogs}
        columns={[
          ["Action", (row) => labelize(row.action)],
          ["Actor", (row) => row.actor?.email || "System"],
          ["Resource", (row) => `${row.resource_type} ${row.resource_id || ""}`],
          ["Date", (row) => formatDate(row.created_at)]
        ]}
      />
    </section>
  );
}

function CommunicationsSection() {
  const [draft, setDraft] = useState({ channel: "email", subject: "", message: "" });
  return (
    <section className="grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
      <form className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xl font-semibold text-accent">Broadcast Center</h2>
        <div className="mt-5 space-y-4">
          <select
            value={draft.channel}
            onChange={(event) => setDraft((prev) => ({ ...prev, channel: event.target.value }))}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm"
          >
            <option value="email">Email</option>
            <option value="sms">SMS</option>
            <option value="whatsapp">WhatsApp</option>
          </select>
          <input
            value={draft.subject}
            onChange={(event) => setDraft((prev) => ({ ...prev, subject: event.target.value }))}
            placeholder="Subject"
            className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm"
          />
          <textarea
            value={draft.message}
            onChange={(event) => setDraft((prev) => ({ ...prev, message: event.target.value }))}
            placeholder="Message"
            rows={8}
            className="w-full rounded-xl border border-slate-200 px-3 py-3 text-sm"
          />
          <button
            type="button"
            disabled
            className="w-full rounded-xl bg-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-[0.18em] text-slate-500"
          >
            Provider setup required
          </button>
        </div>
      </form>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-xl font-semibold text-accent">Support Desk</h2>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          <MetricLine label="Open tickets" value="0" />
          <MetricLine label="Resolved today" value="0" />
          <MetricLine label="Email provider" value="Resend" />
          <MetricLine label="SMS / WhatsApp" value="Pending" />
        </div>
      </div>
    </section>
  );
}

function MetricLine({ label, value }) {
  return (
    <div className="rounded-xl bg-slate-50 px-4 py-3">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">{label}</p>
      <p className="mt-2 text-lg font-semibold text-ink">{value}</p>
    </div>
  );
}

function LedgerTable({ title, rows, columns }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-accent">{title}</h2>
        <span className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-slate-600">
          {rows.length}
        </span>
      </div>
      <div className="mt-5 overflow-x-auto">
        <table className="w-full min-w-[680px] text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-[0.16em] text-slate-500">
              {columns.map(([label]) => (
                <th key={label} className="py-3 pr-4">
                  {label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.slice(0, 20).map((row) => (
              <tr key={`${title}-${row.id}`}>
                {columns.map(([label, render]) => (
                  <td key={label} className="py-3 pr-4 text-slate-700">
                    {render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        {!rows.length && <p className="py-6 text-sm text-slate-500">No records found.</p>}
      </div>
    </section>
  );
}
